import React, { ChangeEvent, FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IBaseComponent } from '../../../sharedInterfaces/IBaseComponent';
import { getDefaultLabelName } from '../../../services/DefaultLabelNameService';
import { ISelectButtons } from '~/sharedInterfaces/SharedInterfaces';
import { IState } from '~/sharedInterfaces/IState';
import ComponentError from '../../ComponentError/ComponentError';
import { pushToGTM } from '~/actions/gtmActions';
import { getInputClassNames, hashCode } from '~/utils';
import { logUserFirstInteraction } from '~/actions/widgetContextActions';
import Tooltip from '../Tooltip/Tooltip';

interface ISelectButtonsProps extends IBaseComponent {
  label?: string;
  value: number;
  buttons: ISelectButtons[];
  analyticsEvent: string;
  handleValidation?: () => void;
  tooltip?: boolean;
}

const SelectButtons: FC<ISelectButtonsProps> = ({
  componentName,
  label,
  value,
  buttons,
  error,
  onChange,
  handleValidation,
  analyticsEvent,
  tooltip,
}) => {
  const { locale, settings } = useSelector((state: IState) => state.widgetContext);
  const componentLabel = label || getDefaultLabelName(locale, componentName).label;
  const componentTooltipText = getDefaultLabelName(locale, componentName).tooltipText;

  const dispatch = useDispatch();

  const handler = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(event);
    dispatch(logUserFirstInteraction());

    dispatch(
      pushToGTM({
        event: `${analyticsEvent}-change`,
        [analyticsEvent]: event.currentTarget.value,
        [`input-type-${analyticsEvent}`]: 'select-buttons',
      })
    );
  };

  useEffect(() => {
    if (handleValidation) handleValidation();
  }, [value]);

  return (
    <div
      className={getInputClassNames({
        tooltip,
        error,
        componentName,
        additionalClassName: 'qred-selectbuttons',
      })}
    >
      {componentTooltipText && <Tooltip tooltip={tooltip} tooltipText={componentTooltipText} />}
      <p className="qred-first">
        <span className="qred-first">{componentLabel}</span>
      </p>
      <div className="qred-group">
        {buttons &&
          buttons.map((button) => {
            const isActive = button.value === value;
            const uniqueKey = hashCode(`${settings.id}${componentName}${button.value}`);
            return (
              <div className={`qred-row${isActive ? ' qred-active' : ''}`} key={button.value}>
                <label htmlFor={uniqueKey}>
                  <span className="qred-first">{button.value}</span>
                  <span className="qred-second">{button.valueType}</span>
                </label>
                <input
                  type="radio"
                  id={uniqueKey}
                  name="term"
                  onChange={handler}
                  value={button.value}
                  checked={button.value === value}
                />
                {error && isActive && <ComponentError error={error} />}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default SelectButtons;
